import { useState, type FormEvent } from "react";
import { useQuery } from "../../api/useQuery.js";
import { getAllCheckItems, getOpenCheckItems } from "../../api/queries.js";
import { createCheckItem } from "../../api/commands.js";
import { PageTitle } from "../../components/ui/PageTitle.js";
import { TaskList } from "../../components/tasks/TaskList.js";
import { QueryStatus } from "../../components/ui/LoadingPlaceholders.js";
import { ViewModeToggle, type ViewMode } from "../../components/ui/ViewModeToggle.js";
import { usePersistedViewMode } from "../../components/ui/usePersistedViewMode.js";
import { Button, Checkbox, Icon, useT } from "@mmm/ui";
export function GlobalTasksScreen() {
  const t = useT();
  const [showCompleted, setShowCompleted] = useState(false);
  const tasks = useQuery(showCompleted ? getAllCheckItems : getOpenCheckItems, undefined);
  const [draft, setDraft] = useState("");
  const [viewMode, setViewMode] = usePersistedViewMode("tasks-page", "list" satisfies ViewMode);

  const visibleTasks = tasks.data || [];

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    // The form's onSubmit invokes this without awaiting it — a failed create
    // is already toasted by commands.ts's run(); keep the draft so the user
    // doesn't lose what they typed.
    try {
      await createCheckItem({ text: draft.trim() });
      setDraft("");
    } catch {
      // Nothing left to do.
    }
  };

  return (
    <div>
      <PageTitle title={t("nav.tasks")} />
      <QueryStatus queries={[tasks]} />

      <section className="mmm-dashboard-panel mmm-task-panel mmm-task-panel--full mmm-page-section">
        <div className="mmm-dashboard-panel-head">
          <div>
            <h2>{t(showCompleted ? "nav.tasks" : "tasks.openTasks")}</h2>
            <p>{t("tasks.shownCount", { count: visibleTasks.length })}</p>
          </div>
          <div className="mmm-panel-actions">
            <Checkbox label={t("tasks.showCompleted")} checked={showCompleted} onChange={(event) => setShowCompleted(event.target.checked)} />
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>
        <form onSubmit={handleAdd} className="mmm-entry-form">
          <span className="mmm-entry-form-icon">
            <Icon name="add" />
          </span>
          <input
            className="mmm-input"
            placeholder={t("tasks.addTaskPlaceholder")}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="primary">
            {t("tasks.addTask")}
          </Button>
        </form>
        <div className="mmm-dashboard-panel-body">
          <TaskList
            items={visibleTasks}
            showSubjectTag
            allowDelete
            loading={tasks.loading}
            hasError={Boolean(tasks.error) && !tasks.waiting}
            onRetry={tasks.retry}
            viewMode={viewMode}
          />
        </div>
      </section>
    </div>
  );
}
